import type { Object3D } from 'three'
import { USDZExporter } from 'three/addons/exporters/USDZExporter.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'

const loader = new GLTFLoader()

export async function loadModel(path: string) {
  const gltf = await loader.loadAsync(path)

  return gltf.scene
}

// Hash fragment used as the Scene Viewer intent's `browser_fallback_url`
// sigil. Same-origin hash-only navigation does NOT reload the page — just
// fires `hashchange` — so we can detect the silent fallback in-place and
// keep the user's session intact. Mirrors the canonical `<model-viewer>`
// implementation (`packages/model-viewer/src/features/ar.ts`).
const NO_AR_FALLBACK_HASH = 'arLaunchFailed'

/**
 * Detect whether an iOS AR Quick Look launch actually opened.
 *
 * Quick Look pulls the page into the background (visibility → hidden); if
 * the anchor click is rejected nothing happens at all. Timeout = failure.
 */
export function detectIntentLaunch(timeoutMs = 1500): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false
    const finish = (launched: boolean) => {
      if (done) return
      done = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      resolve(launched)
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') finish(true)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    setTimeout(() => finish(false), timeoutMs)
  })
}

/**
 * Detect whether a Scene Viewer intent click succeeded.
 *
 * Two signals can resolve us:
 * - `visibilitychange → hidden`: the browser tab was backgrounded, which
 *   means AR Viewer opened. Resolve as success.
 * - `hashchange` to the sigil: Chrome couldn't route the intent and used
 *   the intent's `browser_fallback_url`, which is the current URL with
 *   `#arLaunchFailed` appended. Same-origin hash-only navigation doesn't
 *   reload, so we catch the change in-place and resolve as failure.
 *
 * If neither fires within the timeout, default to success (the hashchange
 * is the authoritative failure signal — its absence likely means AR did
 * launch but the visibility event was missed). Caller is responsible for
 * cleaning the sigil out of the URL on failure.
 */
function detectSceneViewerLaunch(timeoutMs = 3000): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false
    const finish = (launched: boolean) => {
      if (done) return
      done = true
      window.removeEventListener('hashchange', onHashChange)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      resolve(launched)
    }
    const onHashChange = () => {
      if (window.location.hash === '#' + NO_AR_FALLBACK_HASH) finish(false)
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') finish(true)
    }
    window.addEventListener('hashchange', onHashChange)
    document.addEventListener('visibilitychange', onVisibilityChange)
    setTimeout(() => finish(true), timeoutMs)
  })
}

/**
 * Build the intent's `browser_fallback_url`: the current URL plus the
 * hash sigil. Hash-only navigation skips the page reload, which lets us
 * detect failure in-place.
 */
function buildFallbackUrl(currentUrl: URL): string {
  const fallback = new URL(currentUrl.toString())
  fallback.hash = NO_AR_FALLBACK_HASH
  return fallback.toString()
}

/**
 * Strip the failure sigil from the current URL after a detected failure
 * so a subsequent retry / share / refresh doesn't carry it.
 */
function cleanFallbackSigil() {
  if (window.location.hash !== '#' + NO_AR_FALLBACK_HASH) return
  const clean = new URL(window.location.href)
  clean.hash = ''
  window.history.replaceState(window.history.state, '', clean.toString())
}

/**
 * When `?arDebugFail=true` is on the URL we skip the intent click and
 * navigate straight to the would-be fallback URL. That mirrors the state
 * a user lands in when Chrome silently routes to the fallback because no
 * AR handler is registered, letting you test the failure modal on a
 * phone where AR otherwise works. Going via the real intent with a fake
 * package directive is unreliable — Chrome may follow the `scheme=https`
 * fallback (to `arvr.google.com/scene-viewer/…`) and land on a 404
 * instead of using `browser_fallback_url`.
 */
function isArDebugFail(): boolean {
  if (typeof window === 'undefined') return false
  return (
    new URLSearchParams(window.location.search).get('arDebugFail') === 'true'
  )
}

export async function openIOSARQuickLook(model: Object3D): Promise<boolean> {
  const exporter = new USDZExporter()
  let arraybuffer: Uint8Array | undefined

  try {
    arraybuffer = await exporter.parseAsync(model, {
      ar: {
        anchoring: {
          type: 'plane',
        },
        planeAnchoring: {
          alignment: 'horizontal',
        },
      },
    })
  } catch (e) {
    console.error('Error exporting USDZ:', e)
  }

  const blob = new Blob(arraybuffer ? [arraybuffer as BlobPart] : [], {
    type: 'model/vnd.usdz+zip',
  })

  const url = URL.createObjectURL(blob)

  const modelUrl = new URL(url, self.location.toString())

  const anchor = document.createElement('a')
  anchor.setAttribute('rel', 'ar')
  const img = document.createElement('img')
  anchor.appendChild(img)
  anchor.setAttribute('href', modelUrl.toString())
  anchor.style.display = 'none'

  const launched = detectIntentLaunch()
  anchor.click()
  return launched
}

export async function openSceneViewerStatic(
  modelPath: string
): Promise<boolean> {
  const location = self.location.toString()
  const locationUrl = new URL(location)
  const modelUrl = new URL(modelPath!, location)
  if (modelUrl.hash) modelUrl.hash = ''
  const params = new URLSearchParams(modelUrl.search)

  params.set('mode', 'ar_preferred')
  if (!params.has('disable_occlusion')) {
    params.set('disable_occlusion', 'true')
  }
  if (!params.has('link')) {
    params.set('link', locationUrl.toString())
  }

  const detectionPromise = detectSceneViewerLaunch()

  if (isArDebugFail()) {
    window.location.hash = NO_AR_FALLBACK_HASH
    const launched = await detectionPromise
    if (!launched) cleanFallbackSigil()
    return launched
  }

  const intent = `intent://arvr.google.com/scene-viewer/1.0?${
    params.toString() + '&file=' + encodeURIComponent(modelUrl.toString())
  }#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(
    buildFallbackUrl(locationUrl)
  )};end;`

  const anchor = document.createElement('a')
  anchor.setAttribute('href', intent)
  console.log('Attempting to present in AR with Scene Viewer...')
  anchor.click()

  const launched = await detectionPromise
  if (!launched) cleanFallbackSigil()
  return launched
}

export async function openSceneViewerDynamic(
  model: Object3D
): Promise<boolean> {
  // If a Three.js model is provided, export it to GLB and create a Blob URL
  const exporter = new GLTFExporter()
  const glbBuffer: ArrayBuffer = await new Promise((resolve) => {
    exporter.parse(
      model,
      (result) => {
        if (result instanceof ArrayBuffer) resolve(result)
      },
      () => {},
      { binary: true }
    )
  })
  const blob = new Blob([glbBuffer], { type: 'model/gltf-binary' })

  const formData = new FormData()
  formData.append('blob', blob, 'model.glb')

  const modelUrlString = await $fetch('/api/upload/model', {
    method: 'POST',
    body: formData,
  })

  const location = self.location.toString()
  const locationUrl = new URL(location)
  const modelUrl = new URL(modelUrlString, location)
  if (modelUrl.hash) modelUrl.hash = ''
  const params = new URLSearchParams(modelUrl.search)

  params.set('mode', 'ar_preferred')
  if (!params.has('disable_occlusion')) {
    params.set('disable_occlusion', 'true')
  }
  // `link` lets the user jump back to our site from inside Scene Viewer.
  if (!params.has('link')) {
    params.set('link', locationUrl.toString())
  }

  const detectionPromise = detectSceneViewerLaunch()

  if (isArDebugFail()) {
    window.location.hash = NO_AR_FALLBACK_HASH
    const launched = await detectionPromise
    if (!launched) cleanFallbackSigil()
    return launched
  }

  // Pin to the Google app — required for the failure path to work.
  // Without a `package=` directive Chrome converts an unhandled intent
  // to a plain `https://arvr.google.com/scene-viewer/...` URL (which now
  // 404s) instead of using `browser_fallback_url`. With the pin, Chrome
  // sees "package can't handle this" and uses the fallback URL as
  // documented. Matches what canonical `<model-viewer>` ships today.
  const intentUrl = `intent://arvr.google.com/scene-viewer/1.0?${
    params.toString() + '&file=' + encodeURIComponent(modelUrl.toString())
  }#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(
    buildFallbackUrl(locationUrl)
  )};end;`

  console.log('Composed intent URL for Scene Viewer:', intentUrl)

  const arAnchor = document.createElement('a')
  arAnchor.setAttribute('href', intentUrl)
  arAnchor.click()

  const launched = await detectionPromise
  if (!launched) cleanFallbackSigil()
  return launched
}
